import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, DollarSign, Music } from "lucide-react";

/**
 * DashboardScreen — inner UI for the "Artist Dashboard" phone mockup.
 * Animated sales bars + ticking revenue counter to sell the "you keep 100%"
 * story on the homepage.
 */
const BARS = [22, 40, 35, 60, 55, 78, 92];

function Counter({ to, duration = 1.4 }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let start;
    const step = (t) => {
      if (!start) start = t;
      const progress = Math.min(1, (t - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - progress, 3);
      setN(Math.floor(eased * to));
      if (progress < 1) requestAnimationFrame(step);
    };
    const raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return <>{n.toLocaleString()}</>;
}

export default function DashboardScreen() {
  return (
    <div className="relative h-full w-full bg-[#0A0A0A] text-white flex flex-col p-3">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-[8px] uppercase tracking-widest text-gray-500">This month</div>
          <div className="font-display font-black text-lg leading-none mt-0.5">Payouts</div>
        </div>
        <div className="font-mono text-[9px] text-[#22c55e] flex items-center gap-1">
          <TrendingUp className="h-3 w-3" /> +42%
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.6 }}
        className="mb-4"
      >
        <div className="font-display font-black text-3xl tracking-tighter text-[#FF4500] flex items-baseline gap-1">
          <DollarSign className="h-5 w-5" />
          <Counter to={1247} />
          <span className="text-sm text-gray-400 font-mono">.83</span>
        </div>
        <div className="font-mono text-[9px] text-gray-500 mt-0.5">You keep 100% — cashed out weekly</div>
      </motion.div>

      <div className="flex items-end justify-between gap-1.5 h-24 border-b border-white/10 pb-2 mb-3">
        {BARS.map((h, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <motion.div
              className="w-full bg-gradient-to-t from-[#FF4500] to-[#ffb497] rounded-sm origin-bottom"
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ delay: 0.5 + i * 0.08, duration: 0.7, ease: "easeOut" }}
              style={{ height: `${h}%` }}
            />
            <div className="font-mono text-[7px] text-gray-600">
              {["M", "T", "W", "T", "F", "S", "S"][i]}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-1.5">
        {[
          { t: "Anvil Song No. 4", a: "$15.00" },
          { t: "Iron & Ash EP", a: "$15.00" },
          { t: "Burn It Down (Single)", a: "$0.99" },
        ].map((row, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1 + i * 0.15, duration: 0.4 }}
            className="flex items-center justify-between font-mono text-[10px]"
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <Music className="h-2.5 w-2.5 text-[#FF4500] shrink-0" />
              <span className="truncate">{row.t}</span>
            </div>
            <span className="text-[#22c55e]">+{row.a}</span>
          </motion.div>
        ))}
      </div>

      <div className="mt-auto pt-3 flex justify-center">
        <div className="h-1 w-16 rounded-full bg-white/20" />
      </div>
    </div>
  );
}
